import {
  documentTitle,
  hubQueryKeys,
  type InstanceBrandingData,
} from "@lifter/hub-ui/browser-shared";
import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect } from "react";
import { useHubApi } from "../api";

/**
 * Pre-auth white-label state. `GET /api/instance/branding` is public, so the
 * sign-in page can render the tenant logo, accent cascade, and typeface before
 * any session exists. `undefined` while the fetch is in
 * flight or failed — consumers fall back to the deployment-default tokens
 * rather than blocking the boot on branding.
 */
const InstanceBrandingContext = createContext<InstanceBrandingData | undefined>(undefined);

export function useInstanceBrandingQuery() {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.instanceBranding(),
    queryFn: () => api.instanceBranding(),
    staleTime: 5 * 60_000,
  });
}

export function InstanceBrandingProvider({
  branding,
  children,
}: {
  branding: InstanceBrandingData | undefined;
  children: ReactNode;
}) {
  return (
    <InstanceBrandingContext.Provider value={branding}>{children}</InstanceBrandingContext.Provider>
  );
}

export function useInstanceBranding(): InstanceBrandingData | undefined {
  return useContext(InstanceBrandingContext);
}

/**
 * Name the browser tab for this surface, and put back whatever named it before
 * when the surface goes away. The tenant's name comes from the public branding
 * payload, so a surface that renders before branding lands is retitled when it
 * arrives rather than keeping the deployment default indefinitely.
 */
export function useBrandedDocumentTitle(page: string): void {
  const branding = useInstanceBranding();
  const site = branding?.displayName ?? null;

  useEffect(() => {
    const previous = document.title;
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    document.title = documentTitle(page, { displayName: site });
  }, [page, site]);
}
